// src/views/auth/Jugadores.tsx

import React, { useEffect, useState, useContext } from "react";
// Import the custom Axios instance instead of default axios
import axiosInstance from "utils/axiosInstance";
import { AuthContext } from "context/AuthContext"; // Import AuthContext
import axios from "axios";
import Card from "components/card";
import Dropdown from "components/dropdown";
import { AiOutlineUser } from "react-icons/ai";
import { FaPlus } from "react-icons/fa6";
import { toast } from "react-toastify";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { MdEdit } from "react-icons/md";
import Modal from 'components/Modal'
import { format } from "date-fns";
import {
  Button,
  EmptyState,
  Pagination,
  SectionHeader,
  SortIndicator,
  TextField,
  tdClass,
  tdStrongClass,
  thClass,
  thLabelClass,
  theadRowClass,
  trClass,
} from "components/ui";

// ===================== TYPES =====================
type RowObj = {
  id: number;
  name: [string, boolean];
  clasificacion: string;
  quantity: number; // # of Fútbol events
  penas: number;    // # of Peñas events
  date: string;
};

/* ===================== HELPER FOR DATES ===================== */
const formatDate = (isoDate: string) => format(new Date(isoDate), "dd/MM/yy");

// ===================== MODAL COMPONENT =====================

function Jugadores() {
  // Access auth state from context
  const { isLoggedIn } = useContext(AuthContext);

  // ===================== TABLE DATA & SORTING =====================
  const [sorting, setSorting] = useState<SortingState>([]);
  const [data, setData] = useState<RowObj[]>([]);

  // ===================== CREATE PLAYER STATE =====================
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [nuevoJugador, setNuevoJugador] = useState("");

  // ===================== EDIT PLAYER STATE =====================
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editPersona, setEditPersona] = useState<RowObj | null>(null);
  const [nombre, setNombre] = useState("");

  // ===================== DELETE PLAYER STATE =====================
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePersonaId, setDeletePersonaId] = useState<number | null>(null);

  // ===================== PAGINATION =====================
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 9,
  });

  // ===================== FETCH DATA FROM BACKEND =====================
  const fetchPersonData = async () => {
    try {
      // 1) Get data from /eventos/resumen
      const resumenResponse = await axiosInstance.get("/eventos/resumen");
      // 2) Get data from /penas-resumen
      const penasResponse = await axiosInstance.get("/eventos/penas-resumen");
      // 3) Get data from /futbol-resumen
      const futbolResponse = await axiosInstance.get("/eventos/futbol-resumen");

      // Build quick-lookup maps
      const penasMap = penasResponse.data.reduce(
        (map: Record<number, number>, item: any) => {
          map[item.id] = item.penas;
          return map;
        },
        {}
      );
      const futbolMap = futbolResponse.data.reduce(
        (map: Record<number, number>, item: any) => {
          map[item.id] = item.futbol;
          return map;
        },
        {}
      );

      // Merge data
      const transformedData: RowObj[] = resumenResponse.data.map((persona: any) => {


        return {
          id: persona.id,
          name: [persona.nombre, true],
          quantity: futbolMap[persona.id] || 0,
          penas: penasMap[persona.id] || 0,
          date: persona.ultimaAsistencia 
          ? formatDate(persona.ultimaAsistencia)
          : "-", 
        };
      });

      setData(transformedData);
    } catch (error: any) {
      console.error("Error fetching data:", error);
      toast.error("Ocurrió un error al obtener los datos.");
    }
  };

  useEffect(() => {
    fetchPersonData();
  }, []);

  // ===================== DROPDOWN FOR "AGREGAR JUGADOR" =====================
  function AgregarJugador(props: { transparent?: boolean }) {
    const { transparent } = props;
    const [open, setOpen] = React.useState(false);

    return (
      <Dropdown
        button={
          <Button
            variant={transparent ? "ghost" : "soft"}
            size="icon"
            aria-label="Agregar jugador"
            onClick={() => setOpen(!open)}
          >
            <FaPlus className="h-4 w-4" />
          </Button>
        }
        classNames={`${transparent ? "top-8" : "top-11"} right-0 w-max`}
      >
        <div className="z-50 w-max select-none overflow-hidden rounded-card border border-gray-200/80 bg-white p-1.5 shadow-pop dark:border-white/10 dark:!bg-navy-700">
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-2.5 rounded-control px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
            onClick={() => {
              setIsCreateModalOpen(true);
              setNuevoJugador("");
            }}
          >
            <AiOutlineUser className="h-[18px] w-[18px] shrink-0 text-gray-600 dark:text-gray-500" />
            Agregar Jugador
          </button>
        </div>
      </Dropdown>
    );
  }

  // ===================== CREATE JUGADOR =====================
  const handleCrearJugador = async () => {
    if (!nuevoJugador.trim()) {
      toast.error("El nombre del jugador es obligatorio.");
      return;
    }
    const duplicate = data.some(
      (row) =>
        row.name[0].trim().toLowerCase() === nuevoJugador.trim().toLowerCase()
    );
    if (duplicate) {
      toast.error("Ya existe un jugador con ese nombre.");
      return;
    }

    try {
      await axiosInstance.post("/personas", {
        nombre: nuevoJugador,
      });

      setNuevoJugador("");
      setIsCreateModalOpen(false);
      fetchPersonData();
    } catch (error: any) {
      console.error("Error al crear jugador:", error);
      if (axios.isAxiosError(error) && error.response) { // Correct usage
        if (
          error.response.status === 400 &&
          error.response.data?.error?.includes("existe un jugador")
        ) {
          toast.error("Ya existe un jugador con ese nombre.");
        } else {
          toast.error("Error del servidor: " + error.response.data?.error);
        }
      } else {
        toast.error("Ocurrió un error al crear el jugador.");
      }
    }
  };

  // ===================== EDIT JUGADOR =====================
  const handleEdit = (persona: RowObj) => {
    setEditPersona(persona);
    setNombre(persona.name[0]);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editPersona) return;
    if (!nombre.trim()) {
      toast.error("El nombre del jugador es obligatorio.");
      return;
    }
    const duplicate = data.some(
      (row) =>
        row.id !== editPersona.id &&
        row.name[0].trim().toLowerCase() === nombre.trim().toLowerCase()
    );
    if (duplicate) {
      toast.error("Ya existe un jugador con ese nombre.");
      return;
    }

    try {
      await axiosInstance.put(`/personas/${editPersona.id}`, {
        nombre,
      });

      setIsEditModalOpen(false);
      fetchPersonData();
    } catch (error: any) {
      console.error("Error editing persona:", error);
      if (axios.isAxiosError(error) && error.response) { // Correct usage
        if (
          error.response.status === 400 &&
          error.response.data?.error?.includes("existe un jugador")
        ) {
          toast.error("Ya existe un jugador con ese nombre.");
        } else {
          toast.error("Error del servidor: " + error.response.data?.error);
        }
      } else {
        toast.error("Ocurrió un error al editar el jugador.");
      }
    }
  };

  // ===================== DELETE JUGADOR =====================
  const handleDelete = async () => {
    if (!deletePersonaId) return;

    try {
      await axiosInstance.delete(`/personas/${deletePersonaId}`);
      toast.success("Se eliminó con éxito al jugador.");
      setIsDeleteModalOpen(false);
      fetchPersonData();
    } catch (error: any) {
      console.error("Error deleting persona:", error);
      toast.error("Ocurrió un error al eliminar el jugador.");
    }
  };

  // ===================== TABLE SETUP =====================
  const columnHelper = createColumnHelper<RowObj>();
  const columns = [
    // NAME COLUMN
    columnHelper.accessor("name", {
      id: "name",
      header: () => <span className={thLabelClass}>Nombre</span>,
      cell: (info: any) => (
        <div className="flex items-center gap-2">
          {isLoggedIn && (
            <button
              type="button"
              aria-label={`Editar ${info.getValue()[0]}`}
              onClick={() => handleEdit(info.row.original)}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] text-gray-600 transition-colors hover:bg-gray-100 hover:text-brand-500 dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-brand-200"
            >
              <MdEdit className="h-3.5 w-3.5" />
            </button>
          )}
          <span className="truncate font-semibold">{info.getValue()[0]}</span>
        </div>
      ),
    }),
    // ÚLTIMO EVENTO ASISTIDO
    columnHelper.accessor("date", {
      id: "date",
      header: () => <span className={thLabelClass}>Último</span>,
      cell: (info) => (
        <span className="tabular text-gray-700 dark:text-gray-300">
          {info.getValue()}
        </span>
      ),
    }),
    // QUANTITY (FÚTBOL)
    columnHelper.accessor("quantity", {
      id: "quantity",
      header: () => (
        <span className={thLabelClass}>{`PJ ${new Date().getFullYear()}`}</span>
      ),
      cell: (info) => (
        <span className="tabular font-semibold">{info.getValue()}</span>
      ),
    }),
    // PENAS
    columnHelper.accessor("penas", {
      id: "penas",
      header: () => (
        <span className={thLabelClass}>{`Peñas ${new Date().getFullYear()}`}</span>
      ),
      cell: (info) => (
        <span className="tabular font-semibold">{info.getValue()}</span>
      ),
    }),
    // ACTIONS (DELETE)
    columnHelper.display({
      id: "actions",
      cell: (info) => (
        <div className="hidden justify-end sm:flex">
          {isLoggedIn && (
            <Button
              variant="destructive-soft"
              size="sm"
              onClick={() => {
                setDeletePersonaId(info.row.original.id);
                setIsDeleteModalOpen(true);
              }}
              disabled={!isLoggedIn}
            >
              Eliminar
            </Button>
          )}
        </div>
      ),
    }),
  ];

  // Use React Table
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <>
      {/* Main Card */}
      <Card extra="select-none flex h-full w-full flex-col p-5 text-left sm:p-6">
        {/* HEADER */}
        <SectionHeader
          title="Jugadores"
          description="Partidos y peñas de cada jugador en el año en curso."
          actions={isLoggedIn ? <AgregarJugador /> : undefined}
        />

        {/* TABLE WRAPPER */}
        <div className="w-full flex-1">
          {data.length === 0 ? (
            <EmptyState
              icon={<AiOutlineUser />}
              title="Todavía no hay jugadores"
              description={
                isLoggedIn
                  ? "Agregá el primero para empezar a registrar asistencias."
                  : "Cuando se cargue el plantel vas a verlo acá."
              }
              action={
                isLoggedIn ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setIsCreateModalOpen(true);
                      setNuevoJugador("");
                    }}
                  >
                    Agregar jugador
                  </Button>
                ) : undefined
              }
            />
          ) : (
          <div className="-mx-1 overflow-x-auto px-1">
            <table className="w-full sm:min-w-[420px]">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className={theadRowClass}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        onClick={header.column.getToggleSortingHandler()}
                        className={`${thClass}
                          ${!isLoggedIn && (
                            header.column.id === "actions")
                              ? "hidden"
                              : ""
                          }
                          ${isLoggedIn && (
                            header.column.id === "actions")
                              ? "hidden sm:table-cell"
                              : ""
                          }
                        `}
                      >
                        <span className="inline-flex items-center gap-1">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          <SortIndicator direction={header.column.getIsSorted()} />
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className={trClass}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={`${cell.column.id === "name" ? tdStrongClass : tdClass}
                          ${!isLoggedIn && (
                            cell.column.id === "actions")
                              ? "hidden"
                              : ""
                          }
                          ${isLoggedIn && (
                            cell.column.id === "actions")
                              ? "hidden sm:table-cell"
                              : ""
                          }
                        `}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {/* PAGINATION CONTROLS */}
        <Pagination
          className="mt-5 border-t border-gray-200/70 pt-5 dark:border-white/10"
          pageIndex={table.getState().pagination.pageIndex}
          pageCount={table.getPageCount()}
          canPreviousPage={table.getCanPreviousPage()}
          canNextPage={table.getCanNextPage()}
          onPreviousPage={() => table.previousPage()}
          onNextPage={() => table.nextPage()}
        />
      </Card>

      {/* ========== CREATE PLAYER MODAL ========== */}
      {isLoggedIn && (
        <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)}>
          <h3 className="select-none text-[17px] font-semibold text-gray-900 dark:text-white">
            Crear jugador
          </h3>
          <p className="mb-5 mt-1 select-none text-sm text-gray-700 dark:text-gray-400">
            Va a quedar disponible para marcar asistencias.
          </p>
          <TextField
            id="nuevo-jugador"
            label="Nombre"
            autoFocus
            value={nuevoJugador}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNuevoJugador(e.target.value)}
            placeholder="Introduce el nombre"
          />
          <div className="mt-6 flex select-none justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleCrearJugador}>
              Crear
            </Button>
          </div>
        </Modal>
      )}

      {/* ========== EDIT PLAYER MODAL ========== */}
      {isLoggedIn && (
        <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
          <h3 className="mb-5 select-none text-[17px] font-semibold text-gray-900 dark:text-white">
            Editar jugador
          </h3>
          <TextField
            id="nombre"
            label="Nombre"
            autoFocus
            value={nombre}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNombre(e.target.value)}
            placeholder="Escribe un nombre"
          />
          <div className="mt-6 flex select-none justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSaveEdit}>
              Guardar cambios
            </Button>
          </div>
        </Modal>
      )}

      {/* ========== DELETE CONFIRMATION MODAL ========== */}
      {isLoggedIn && (
        <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
          <h3 className="select-none text-[17px] font-semibold text-gray-900 dark:text-white">
            ¿Dar de baja al jugador?
          </h3>
          <p className="mt-2 select-none text-sm leading-relaxed text-gray-700 dark:text-gray-300">
            Esta acción hará descender al jugador. Vas a poder reactivarlo desde
            la lista de inactivos.
          </p>
          <div className="mt-6 flex select-none justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Dar de baja
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}

export default Jugadores;
